import type { Contract } from './contract';
import { kindSelectors, readPath } from './split-by-field';
import { readBill } from './xml';
import type { RouteDef, Sample } from './types';

/** How one endpoint is run on QA: the shop its bills come from, the values they carry, the checks left out. */
export interface QaSettings {
  tenantId: string;
  businessId: string;
  /** A payload field or address part with this name gets this value, e.g. `mobile`. */
  values: Record<string, string>;
  /** Cases left unticked, by id. */
  skip?: string[];
}

/** What must be true of a GET endpoint's answer once an endpoint's bills have gone through. */
export interface QaCheck {
  routeId: string;
  field: string;
  expect: 'exists' | 'equals' | 'increases';
  value?: string;
}

export interface QaCase {
  id: string;
  name: string;
  payload: string;
  expectation: 'pass' | 'reject';
  /** The field a refusal must name. */
  field?: string;
}

const isNode = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const MOST_BAD_CASES = 20;

/** The bill with the QA values put in, and a bill number nobody has used. */
const freshen = (payload: unknown, settings: QaSettings, reference: string | undefined, number: string): unknown => {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!isNode(node)) return node;
    return Object.fromEntries(
      Object.entries(node).map(([key, value]) => {
        if (key === reference && !isNode(value) && !Array.isArray(value)) return [key, number];
        if (Object.prototype.hasOwnProperty.call(settings.values, key) && !isNode(value) && !Array.isArray(value))
          return [key, settings.values[key]];
        return [key, walk(value)];
      }),
    );
  };
  return walk(payload);
};

const xmlEscape = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** An XML bill with the QA values put in: the text of each named leaf element is replaced, nothing else moves. */
const freshenXml = (xml: string, settings: QaSettings, reference: string | undefined, number: string): string => {
  const values = new Map(Object.entries(settings.values));
  if (reference) values.set(reference, number);
  let out = xml;
  for (const [name, value] of values) {
    if (!/^[A-Za-z_][\w.-]*$/.test(name)) continue;
    out = out.replace(
      new RegExp(`(<${name}(?:\\s[^>]*)?>)([^<>]*)(</${name}>)`, 'g'),
      (_all, open: string, _old, close: string) => `${open}${xmlEscape(value)}${close}`,
    );
  }
  return out;
};

/** The bill without one field, wherever `a.b` or `items[].b` finds it. */
export const withoutField = (payload: unknown, path: string): unknown => {
  const drop = (node: unknown, parts: string[]): unknown => {
    if (Array.isArray(node)) return node.map((item) => drop(item, parts));
    if (!isNode(node) || parts.length === 0) return node;
    const [part, ...rest] = parts;
    const key = part.replace(/\[\]$/, '');
    if (!(key in node)) return node;
    if (rest.length === 0) return Object.fromEntries(Object.entries(node).filter(([name]) => name !== key));
    return { ...node, [key]: drop(node[key], rest) };
  };
  return drop(payload, path.split('.'));
};

/** Whether a refusal's path names the field, however indexes are written: `[0].items[1].b` is `items[].b`. */
export const namesField = (violation: string, field: string): boolean => {
  const bare = (path: string) =>
    path
      .replace(/^\$?[./]?/, '')
      .replace(/\[\d*]/g, '')
      .replace(/^\./, '');
  return bare(violation) === bare(field) || bare(violation).endsWith(`.${bare(field)}`);
};

/**
 * Every case a QA run sends for one endpoint, built from its configuration: each sample as it is,
 * then one bill per required field with that field left out, which must be refused naming it.
 */
export const qaCases = (input: {
  route: Pick<RouteDef, 'referenceField'> & { format?: 'xml' };
  samples: Sample[];
  settings: QaSettings;
  runId: string;
  rules?: Contract;
  kindRules?: Record<string, Contract>;
  flow?: string;
}): QaCase[] => {
  const reference = input.route.referenceField
    ?.split('.')
    .at(-1)
    ?.replace(/\[\d*]$/, '');
  let count = 0;
  if (input.route.format === 'xml') {
    /* Each sample goes as the vendor wrote it. Leaving a field out of a bill needs the bill's own markup edited, which JSON endpoints only do for now. */
    return input.samples.flatMap((sample) => {
      try {
        readBill(sample.payload, 'xml');
      } catch {
        return [];
      }
      return [
        {
          id: `sample-${sample.id}`,
          name: sample.name,
          payload: freshenXml(sample.payload, input.settings, reference, `QA-${input.runId}-${++count}`),
          expectation: sample.expectation ?? 'pass',
          ...(sample.expectedViolationPath ? { field: sample.expectedViolationPath } : {}),
        },
      ];
    });
  }
  const fresh = (payload: unknown) => freshen(payload, input.settings, reference, `QA-${input.runId}-${++count}`);
  const readable = input.samples.flatMap((sample) => {
    try {
      return [{ sample, payload: JSON.parse(sample.payload) as unknown }];
    } catch {
      return [];
    }
  });

  const cases: QaCase[] = readable.map(({ sample, payload }) => ({
    id: `sample-${sample.id}`,
    name: sample.name,
    payload: JSON.stringify(fresh(payload)),
    expectation: sample.expectation ?? 'pass',
    ...(sample.expectedViolationPath ? { field: sample.expectedViolationPath } : {}),
  }));

  const good = readable.filter(({ sample }) => sample.expectation !== 'reject');
  const bad = (label: string, contract: Contract | undefined, base: unknown) =>
    (contract?.fields ?? [])
      .filter((field) => field.required)
      .map((field) => ({
        id: `missing-${label}-${field.path}`,
        name: `${label === 'every' ? 'A bill' : `A ${label} bill`} without ${field.path}`,
        payload: JSON.stringify(fresh(withoutField(base, field.path))),
        expectation: 'reject' as const,
        field: field.path,
      }));

  if (good[0]) cases.push(...bad('every', input.rules, good[0].payload));
  for (const { kind, path, value } of kindSelectors(input.flow)) {
    const base = good.find(({ payload }) => readPath(payload, path) === value)?.payload;
    if (base !== undefined) cases.push(...bad(kind, input.kindRules?.[kind], base));
  }
  const samples = cases.filter((item) => item.id.startsWith('sample-'));
  return [...samples, ...cases.filter((item) => !item.id.startsWith('sample-')).slice(0, MOST_BAD_CASES)];
};

/** An address with its `{parts}` filled from the QA values, or the name of the first one missing. */
export const qaAddress = (path: string, settings: QaSettings): { path: string } | { missing: string } => {
  const missing = [...path.matchAll(/\{([^}]+)}/g)].map((match) => match[1]).find((name) => !settings.values[name]);
  if (missing) return { missing };
  return { path: path.replace(/\{([^}]+)}/g, (_whole, name: string) => encodeURIComponent(settings.values[name])) };
};

/** Whether a follow-up check holds, given the field's value before the bills and after them. */
export const qaCheckHolds = (check: QaCheck, before: string | undefined, after: string | undefined): boolean => {
  if (check.expect === 'exists') return after !== undefined && after !== '';
  if (check.expect === 'equals') return after === (check.value ?? '');
  const was = Number(before);
  const now = Number(after);
  return Number.isFinite(was) && Number.isFinite(now) && now > was;
};

export const qaSettingsFault = (settings: Partial<QaSettings> | undefined): string | undefined => {
  if (!settings?.tenantId?.trim() || !settings.businessId?.trim()) return 'Choose the shop these QA bills come from.';
  return undefined;
};

/** The names this endpoint's bills and address carry, offered as the fields a QA value can fill. */
export const qaFieldNames = (samples: Sample[], path: string): string[] => {
  const names = new Set([...path.matchAll(/\{([^}]+)}/g)].map((match) => match[1]));
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) node.forEach(walk);
    else if (isNode(node))
      Object.entries(node).forEach(([key, value]) => {
        if (isNode(value) || Array.isArray(value)) walk(value);
        else names.add(key);
      });
  };
  for (const sample of samples) {
    try {
      walk(JSON.parse(sample.payload));
    } catch {
      /* a sample that is not JSON offers no fields */
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
};

export interface QaCaseResult {
  id: string;
  name: string;
  expectation: 'pass' | 'reject';
  field?: string;
  ok: boolean;
  /** What happened, in a sentence. */
  said: string;
  status?: number;
  /** Where an accepted bill was really sent. */
  sentTo?: string[];
  steps?: { name: string; status: string; detail?: string | null }[];
}

export interface QaCheckResult extends QaCheck {
  name: string;
  before?: string;
  after?: string;
  ok: boolean;
  said: string;
}

export interface QaRun {
  id: string;
  routeId: string;
  startedAt: string;
  finishedAt: string;
  startedBy: string;
  passed: number;
  total: number;
  cases: QaCaseResult[];
  checks: QaCheckResult[];
}
